// Letris — share helpers (Web Share API + canvas card + emoji block + toast)

const SITE = (typeof location !== 'undefined' && location.origin && !location.origin.startsWith('file://'))
  ? location.origin
  : 'https://letris.app';

export function siteUrl(path = '/') { return SITE.replace(/\/$/, '') + path; }

let toastTimer = null;
export function toast(msg, kind = 'ok') {
  let host = document.getElementById('lt-toast-host');
  if (!host) {
    host = document.createElement('div');
    host.id = 'lt-toast-host';
    host.className = 'lt-toast-host';
    document.body.appendChild(host);
  }
  host.innerHTML = '';
  const node = document.createElement('div');
  node.className = 'lt-toast lt-toast--' + kind;
  node.textContent = msg;
  host.appendChild(node);
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { node.classList.add('lt-toast--out'); setTimeout(() => host.remove(), 350); }, 2400);
}

export async function shareText({ title, text, url, fileBlob, fileName }) {
  const fullText = url ? (text + '\n' + url) : text;
  const data = { title, text, url };
  const files = (fileBlob && fileName && typeof File !== 'undefined')
    ? [new File([fileBlob], fileName, { type: fileBlob.type || 'image/png' })]
    : null;
  if (files && navigator.canShare && navigator.canShare({ files })) {
    try { await navigator.share({ ...data, files }); return 'shared'; } catch (e) { if (e?.name === 'AbortError') return 'aborted'; }
  }
  if (navigator.share) {
    try { await navigator.share(data); return 'shared'; } catch (e) { if (e?.name === 'AbortError') return 'aborted'; }
  }
  try {
    await navigator.clipboard.writeText(fullText);
    toast('Texto copiado al portapapeles');
    return 'copied';
  } catch {
    window.prompt('Copia y comparte:', fullText);
    return 'prompt';
  }
}

// Wordle-style emoji block for daily mode
export function dailyEmojiBlock(targets, found) {
  const ok = '🟩', miss = '⬛';
  return targets.map(w => found.includes(w) ? ok : miss).join('');
}

// Render a 1080x1080 share card to PNG blob
export async function renderShareCard({
  kicker = 'LETRIS',
  title = '',
  bigStat = '',
  bigStatLabel = '',
  rows = [],          // [[label, value], ...]
  accent = '#06D6A0',
  url = siteUrl('/'),
  emojiBlock = '',    // optional
}) {
  const W = 1080, H = 1080;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d');

  // bg
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, '#0e0e14');
  grad.addColorStop(1, '#18181e');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // grid pattern
  ctx.strokeStyle = '#1a1a22';
  ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 48) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
  for (let y = 0; y < H; y += 48) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

  // accent corner block
  ctx.save();
  ctx.translate(W - 280, -50);
  ctx.rotate(-0.18);
  ctx.fillStyle = accent;
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 8;
  ctx.fillRect(0, 0, 360, 360);
  ctx.strokeRect(0, 0, 360, 360);
  ctx.restore();

  // kicker badge
  ctx.fillStyle = '#FFD166';
  ctx.fillRect(80, 80, 380, 64);
  ctx.strokeStyle = '#000'; ctx.lineWidth = 5;
  ctx.strokeRect(80, 80, 380, 64);
  ctx.fillStyle = '#000';
  ctx.font = '900 28px "Courier New", monospace';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(kicker, 270, 116);

  // big logo / title text
  ctx.textAlign = 'left'; ctx.textBaseline = 'top';
  ctx.fillStyle = accent;
  ctx.fillRect(80, 200, 920, 180);
  ctx.strokeStyle = '#000'; ctx.lineWidth = 10;
  ctx.strokeRect(80, 200, 920, 180);
  ctx.fillStyle = '#000';
  ctx.font = '900 130px "Courier New", monospace';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(title.toUpperCase().slice(0, 12), 540, 290);

  // big stat
  ctx.textAlign = 'left'; ctx.textBaseline = 'top';
  ctx.fillStyle = '#fff';
  ctx.font = '800 32px "Courier New", monospace';
  ctx.fillText(bigStatLabel.toUpperCase(), 80, 430);
  ctx.fillStyle = accent;
  ctx.font = '900 180px "Courier New", monospace';
  ctx.fillText(bigStat, 80, 470);

  // stat rows
  let y = 700;
  ctx.font = '800 28px "Courier New", monospace';
  for (const [label, value] of rows) {
    ctx.fillStyle = '#a0a0a8';
    ctx.fillText(label.toUpperCase(), 80, y);
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'right';
    ctx.fillText(String(value), W - 80, y);
    ctx.textAlign = 'left';
    ctx.fillStyle = '#2a2a32';
    ctx.fillRect(80, y + 42, W - 160, 2);
    y += 60;
  }

  // emoji block (daily)
  if (emojiBlock) {
    ctx.textAlign = 'center';
    ctx.font = '700 56px "Courier New", monospace';
    ctx.fillStyle = '#fff';
    ctx.fillText(emojiBlock, W / 2, y + 20);
    y += 90;
  }

  // footer URL
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, H - 90, W, 90);
  ctx.fillStyle = '#000';
  ctx.font = '900 32px "Courier New", monospace';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('LETRIS · ' + url.replace(/^https?:\/\//, ''), W / 2, H - 45);

  return await new Promise(res => cv.toBlob(b => res(b), 'image/png', 0.95));
}
